import { combineReducers, createReducer, ReducerOptionalMapObject } from 'store/createReducer';
import initialPageState from 'components/Pagelayout/initialPageState';
import { CheckoutPageModel } from 'models/CheckoutPageModel';
import { AddressInfo } from 'models/AddressInfo';
import { PaymentMethod } from 'models/PaymentMethod';
import {
  CHECKOUT_HIDE_DELIVERY_OPTIONS,
  CHECKOUT_SUGGESTED_ADDRESSES_LOADED,
  SuggestedAddressesAction,
  CHECKOUT_SUGGESTED_CLEAR,
  CHECKOUT_SUGGESTED_FIELD,
  SuggestedFieldAction,
  PurchaseResponseAction,
  CHECKOUT_PURCHASE_RESPONSE,
  CHECKOUT_PURCHASE_START,
  CHECKOUT_PURCHASE_CLOSE_MODAL,
  CHECKOUT_QUICKSTART_SET,
  CheckoutQuickStartAction,
  CHECKOUT_SINGLEPRODUCT_SET,
  CHECKOUT_UPDATE,
  CheckoutUpdateAction,
  CHECKOUT_MODAL_ADDRESS_TOO_LONG_NAME,
  CheckoutTooLongNameAction
} from './actions';
import { CheckoutDeliveryItem } from 'models/CheckoutDeliveryItem';
import {
  CHECKOUT_ADDRESS_INFO_MULTIPLE,
  CHECKOUT_ADDRESS_INFO_SET,
  CheckoutAddressInfoAction,
  CHECKOUT_CART_ITEMS_SET,
  CHECKOUT_CART_ITEMS_REMOVE,
  CheckoutCartItemsAction,
  CHECKOUT_CART_ITEM_UPDATE,
  CheckoutUpdateCartItemsAction,
  CheckoutSetCartItemsAction,
  CHECKOUT_CART_ITEM_QUANTITY_UPDATE,
  CheckoutQuantityUpdateAction
} from 'views/CheckoutPage/actions';
import { SuggestedAddress } from 'models/SuggestedAddress';
import { PurchaseResponse } from 'models/PurchaseResponse';
import { CartItem } from 'models/CartItem';
import { CheckoutCartItem } from 'models/CheckoutCartItem';
import { ProductUnfulfilled } from 'models/ProductUnfulfilled';

const addressInfo = createReducer<Partial<AddressInfo>>(
  {},
  {
    [CHECKOUT_ADDRESS_INFO_SET]: (
      state: AddressInfo,
      { addressInfo }: CheckoutAddressInfoAction
    ) => ({
      ...state,
      ...addressInfo
    }),
    [CHECKOUT_ADDRESS_INFO_MULTIPLE]: (state, action: CheckoutAddressInfoAction) => {
      return {
        ...state,
        addresses: action.addressInfo
      };
    },
    [CHECKOUT_MODAL_ADDRESS_TOO_LONG_NAME]: (state, { address }: CheckoutTooLongNameAction) => {
      return { ...state, ...address, isTooLongName: address.isTooLongName };
    }
  }
);

const deliveries = createReducer<CheckoutDeliveryItem[]>([], {
  [CHECKOUT_HIDE_DELIVERY_OPTIONS]: () => [],
  [CHECKOUT_UPDATE]: (state, { deliveries }: CheckoutUpdateAction) => {
    return deliveries || state;
  }
});

const unfulfilledProducts = createReducer<ProductUnfulfilled[]>([], {
  [CHECKOUT_UPDATE]: (state, { productFulfilledResult }) => {
    return productFulfilledResult || state;
  }
});

const paymentMethods = createReducer<PaymentMethod[]>([], {
  [CHECKOUT_UPDATE]: (state, { paymentMethods }: CheckoutUpdateAction) => {
    return paymentMethods || state;
  }
});

const suggestedAddresses = createReducer<SuggestedAddress[]>([], {
  [CHECKOUT_SUGGESTED_ADDRESSES_LOADED]: (_, action: SuggestedAddressesAction) => {
    return action.addresses;
  },
  [CHECKOUT_SUGGESTED_CLEAR]: () => []
});

const suggestedForField = createReducer<string>('', {
  [CHECKOUT_SUGGESTED_FIELD]: (_state: string, action: SuggestedFieldAction) => {
    return action.fieldName;
  }
});

const checkoutCartItems = createReducer<CartItem[]>([], {
  [CHECKOUT_CART_ITEMS_SET]: (
    _state: CheckoutCartItem[],
    { cartItems }: CheckoutSetCartItemsAction
  ) => {
    return cartItems;
  },
  [CHECKOUT_CART_ITEMS_REMOVE]: (
    state: CheckoutCartItem[],
    { productId, cartItemId, removed }: CheckoutCartItemsAction
  ) => {
    return state.map(item => {
      return item.productId === productId && item.cartItemId === cartItemId
        ? { ...item, removed }
        : item;
    });
  },
  [CHECKOUT_CART_ITEM_UPDATE]: (
    state: CheckoutCartItem[],
    { cartItem, checkoutCartItemId }: CheckoutUpdateCartItemsAction
  ) => {
    const checkoutCartItems = state.map(item =>
      item.cartItemId === checkoutCartItemId ? { ...cartItem, removed: false } : item
    );
    return checkoutCartItems;
  },
  [CHECKOUT_CART_ITEM_QUANTITY_UPDATE]: (
    state: CheckoutCartItem[],
    { cartItemId, amount }: CheckoutQuantityUpdateAction
  ) => {
    return state.map(item =>
      item.cartItemId === cartItemId ? { ...item, quantity: { ...item.quantity, amount } } : item
    );
  }
});

const showDeliveries = createReducer<boolean>(false, {
  [CHECKOUT_HIDE_DELIVERY_OPTIONS]: () => false,
  [CHECKOUT_UPDATE]: (state, { deliveries }: CheckoutUpdateAction) =>
    deliveries === undefined ? state : Boolean(deliveries)
});

const purchaseResponse = createReducer<PurchaseResponse>(
  {
    valid: false,
    message: '',
    content: ''
  },
  {
    [CHECKOUT_PURCHASE_RESPONSE]: (
      state: PurchaseResponse,
      { response: { expiredInWebShopProducts: _, ...response } }: PurchaseResponseAction
    ) => {
      return {
        ...state,
        ...response
      };
    },
    [CHECKOUT_PURCHASE_START]: (
      state: PurchaseResponse,
      { response: { valid, message } }: PurchaseResponseAction
    ) => {
      return { ...state, valid, message };
    },
    [CHECKOUT_PURCHASE_CLOSE_MODAL]: (state: PurchaseResponse) => {
      return {
        ...state,
        valid: false,
        message: ''
      };
    }
  }
);

const summary = createReducer<CheckoutPageModel['summary']>(
  { charges: [], total: 0, totalIncludingVat: 0 },
  {
    [CHECKOUT_UPDATE]: (state, { summary }: CheckoutUpdateAction) => summary || state
  }
);
const isQuickStart = createReducer<boolean>(true, {
  [CHECKOUT_QUICKSTART_SET]: (_state, { value }: CheckoutQuickStartAction) => value
});
const isSingleProduct = createReducer<boolean>(false, {
  [CHECKOUT_SINGLEPRODUCT_SET]: (_state, { value }: CheckoutQuickStartAction) => value
});

const userInfo = createReducer<CheckoutPageModel['userInfo']>(
  { displayName: '', userName: '' },
  {
    [CHECKOUT_UPDATE]: (state, { userInfo }: CheckoutUpdateAction) => userInfo || state
  }
);

export default combineReducers<CheckoutPageModel, ReducerOptionalMapObject>({
  ...initialPageState,
  initialValues: {},
  isZipCodeDependent: false,
  disableCardPaymentROTFeatureToggle: false,
  removeCheckoutMessageBoxFeatureToggle: false,
  isBankIdActive: false,
  isSingleProduct,
  deliveries,
  unfulfilledProducts,
  addressInfo,
  showDeliveries,
  paymentMethods,
  suggestedAddresses,
  suggestedForField,
  purchaseResponse,
  isQuickStart,
  checkoutCartItems,
  summary,
  userInfo
});
